import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type {
  ChatAppResponse,
  Document,
  DocumentReferencePreview,
  ResponseContextDataPoint,
} from "@/api/models"

import { Newspaper } from "@/components/icons"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import { LibraryDocumentsSection } from "./citation-sections/library-documents-section"
import { M365SourcesSection } from "./citation-sections/m365-sources-section"
import { UploadedFilesSection } from "./citation-sections/uploaded-files-section"
import { UserDocumentsSection } from "./citation-sections/user-documents-section"
import { WebSourcesSection } from "./citation-sections/web-sources-section"
import { MetadataDialog } from "./metadata-dialog"
import { SupportingContent } from "./supporting-content"
import { buttonVariants } from "../../../components/ui/button-variants"
import { useCitationPreview } from "../contexts/use-citation-preview"
import { useFindChunkForCitation } from "../hooks/use-chunk-finder"
import { useCitation } from "../hooks/use-citation"
import { categorizeCitations } from "../utils/citation-categorizer"

type CitationComponentProps = {
  citations: string[]
  documents: [string, Document][]
  citationSection?: string
  answer?: ChatAppResponse
  messageIndex?: number
}

export type CitationHandle = {
  scrollToCitation: (index: number) => void
  scrollToReferenceList: (index: number) => void
}

export const Citation = forwardRef<CitationHandle, CitationComponentProps>(
  ({ citations, documents, citationSection, answer, messageIndex }, ref) => {
    const { t } = useTranslation()
    const [openChunkModal, setOpenChunkModal] = useState(false)
    const [selectedChunkData, setSelectedChunkData] = useState<
      ResponseContextDataPoint | { text: string[]; images?: { url: string }[] } | null
    >(null)
    const [accordionValue, setAccordionValue] = useState<string | undefined>(undefined)
    const [highlightedCitationIndex, setHighlightedCitationIndex] = useState<number | null>(null)
    const citationContainerRef = useRef<HTMLElement | null>(null)

    const {
      getDocumentExtraData,
      handleDownloadClicked,
      openMetadataDialog,
      setOpenMetadataDialog,
      extraData,
      setExtraData,
      formatCitationDisplay,
    } = useCitation()

    const { openPreview } = useCitationPreview()
    const { findChunkForUrlCitation } = useFindChunkForCitation(answer)

    const handleShowChunk = (citation: string) => {
      const chunkData = findChunkForUrlCitation(citation)
      if (chunkData) {
        setSelectedChunkData(chunkData)
        setOpenChunkModal(true)
      }
    }

    const hasChunkData = (citation: string): boolean => {
      return !!findChunkForUrlCitation(citation)
    }

    // Categorize citations by source type
    const categorizedCitations = categorizeCitations(citations, documents)
    const documentReferences: DocumentReferencePreview[] = answer?.context.document_references ?? []

    const scrollToCitation = useCallback(
      (index: number) => {
        const messageScopedSelector = `.supContainer[data-citation-index="${index}"]`
        const messageScopedRoot = citationContainerRef.current?.closest("[data-message-id]")
        const scopedElement =
          messageScopedRoot instanceof HTMLElement
            ? messageScopedRoot.querySelector(messageScopedSelector)
            : null

        const messageIndexSelector =
          messageIndex !== undefined
            ? `.supContainer[data-citation-index="${index}"][data-message-index="${messageIndex}"]`
            : null
        const indexedElement = messageIndexSelector
          ? document.querySelector(messageIndexSelector)
          : null
        const globalFallbackElement = document.querySelector(messageScopedSelector)
        const element = indexedElement ?? scopedElement ?? globalFallbackElement

        if (element instanceof HTMLElement) {
          element.scrollIntoView({ behavior: "smooth", block: "center" })
          element.focus({ preventScroll: true })
        }
      },
      [messageIndex]
    )

    const scrollToReferenceList = useCallback(
      (index: number) => {
        setAccordionValue("references")

        window.setTimeout(() => {
          const referenceSelector =
            messageIndex !== undefined
              ? `[data-citation-index="${index}"][data-message-index="${messageIndex}"]`
              : `[data-citation-index="${index}"]`
          const listContainer = citationContainerRef.current
          const referenceElement = listContainer?.querySelector(referenceSelector)
          if (referenceElement instanceof HTMLElement) {
            referenceElement.scrollIntoView({ behavior: "smooth", block: "center" })
          }

          setHighlightedCitationIndex(index)
          window.setTimeout(() => {
            setHighlightedCitationIndex(null)
          }, 2500)
        }, 150)
      },
      [messageIndex]
    )

    useImperativeHandle(ref, () => ({
      scrollToCitation,
      scrollToReferenceList,
    }))

    if (citations.length > 0) {
      return (
        <section ref={citationContainerRef}>
          <Accordion
            collapsible
            type="single"
            value={accordionValue}
            onValueChange={setAccordionValue}
          >
            <AccordionItem
              className="border-border/10 rounded-md data-[state=closed]:border-none data-[state=open]:rounded-lg data-[state=open]:border"
              value="references"
            >
              <AccordionTrigger
                data-testid="references-accordion-trigger"
                className={cn(
                  "mb-1 text-base font-bold hover:no-underline",
                  buttonVariants({
                    className:
                      "hover:text-foreground items-center justify-between px-3 font-bold hover:bg-transparent hover:underline hover:underline-offset-4",
                    variant: "icon",
                  })
                )}
              >
                <section className="flex items-center gap-2">
                  <Newspaper className=":rotate-0 size-5 shrink-0" />
                  {t("answer.references")} ({citations.length})
                </section>
              </AccordionTrigger>
              <AccordionContent className="relative">
                <div className="flex max-h-[500px] flex-col gap-4 overflow-y-auto rounded px-3">
                  <WebSourcesSection
                    hasChunkData={hasChunkData}
                    highlightedIndex={highlightedCitationIndex}
                    messageIndex={messageIndex}
                    sources={categorizedCitations.webSources}
                    onNavigateToInline={scrollToCitation}
                    onShowChunk={handleShowChunk}
                  />

                  <M365SourcesSection
                    documents={documents}
                    highlightedIndex={highlightedCitationIndex}
                    messageIndex={messageIndex}
                    sources={categorizedCitations.m365Sources}
                    onNavigateToInline={scrollToCitation}
                  />

                  <LibraryDocumentsSection
                    citationSection={citationSection ?? ""}
                    documents={categorizedCitations.libraryDocuments}
                    documentsList={documents}
                    findChunkForUrlCitation={findChunkForUrlCitation}
                    formatCitationDisplay={formatCitationDisplay}
                    getDocumentExtraData={getDocumentExtraData}
                    handleDownloadClicked={handleDownloadClicked}
                    highlightedIndex={highlightedCitationIndex}
                    messageIndex={messageIndex}
                    openPreview={openPreview}
                    setExtraData={setExtraData}
                    setOpenMetadataDialog={setOpenMetadataDialog}
                    t={t}
                    onNavigateToInline={scrollToCitation}
                  />

                  <UserDocumentsSection
                    citationSection={citationSection ?? ""}
                    documentReferences={documentReferences}
                    documents={categorizedCitations.userDocuments}
                    formatCitationDisplay={formatCitationDisplay}
                    handleDownloadClicked={handleDownloadClicked}
                    highlightedIndex={highlightedCitationIndex}
                    messageIndex={messageIndex}
                    openPreview={openPreview}
                    setExtraData={setExtraData}
                    setOpenMetadataDialog={setOpenMetadataDialog}
                    t={t}
                    onNavigateToInline={scrollToCitation}
                  />

                  <UploadedFilesSection
                    citationSection={citationSection ?? ""}
                    documentsList={documents}
                    files={categorizedCitations.uploadedFiles}
                    formatCitationDisplay={formatCitationDisplay}
                    getDocumentExtraData={getDocumentExtraData}
                    handleDownloadClicked={handleDownloadClicked}
                    highlightedIndex={highlightedCitationIndex}
                    messageIndex={messageIndex}
                    openPreview={openPreview}
                    setExtraData={setExtraData}
                    setOpenMetadataDialog={setOpenMetadataDialog}
                    t={t}
                    onNavigateToInline={scrollToCitation}
                  />
                </div>
                <div className="from-background pointer-events-none absolute right-0 bottom-0 left-0 h-6 bg-linear-to-t to-transparent" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <MetadataDialog
            extraData={extraData}
            openMetadataDialog={openMetadataDialog}
            setOpenMetadataDialog={setOpenMetadataDialog}
          />

          <Dialog open={openChunkModal} onOpenChange={setOpenChunkModal}>
            <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-4xl">
              <DialogHeader>
                <DialogTitle>{t("answer.chunk_content")}</DialogTitle>
              </DialogHeader>
              {selectedChunkData && <SupportingContent supportingContent={selectedChunkData} />}
            </DialogContent>
          </Dialog>
        </section>
      )
    }
  }
)

Citation.displayName = "Citation"
